import { getToken, onMessage } from 'firebase/messaging';
import { getMessagingInstance } from '../firebase/config';
import { apiClient, API_ENDPOINTS } from '../config/api';

/**
 * FCM web push service for rialty-static (SP-facing web app).
 *
 * Mobile has FCM listeners on both the list and detail screens to keep SR
 * state fresh under the SP without polling; web deliberately dropped
 * polling/focus refresh on 2026-08-31 EOD ("automatic api call nahi
 * karawo"). This module adds the equivalent push-based path — silent
 * in the background, and event-driven in the foreground — so JobsPage
 * and JobDetailPage can subscribe once and refresh only when a real
 * server-side change fires a push.
 *
 * Failure modes are silent: unsupported browser, denied permission,
 * missing VAPID key, service-worker registration failure, backend
 * token-registration failure — all log a warning and continue. The
 * page-level onMessage subscribers just never fire, and the SP falls
 * back to the pre-existing behavior (fresh state on JobDetail mount,
 * user-initiated refresh via failed accept/decline).
 */

const DEVICE_ID_KEY = 'rialty:webPushDeviceId';
const SW_URL_BASE = '/firebase-messaging-sw.js';

/**
 * Stable identifier for this browser install. Backend upserts a per-device
 * row keyed on this id (see FcmTokenService.registerToken) — persisting to
 * localStorage keeps the same slot instead of piling up new tokens every
 * login.
 */
const getOrCreateDeviceId = () => {
  try {
    let existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    // crypto.randomUUID is available in all modern browsers Safari/Chrome/
    // Firefox on the desktop range we support; fall back to a timestamp-
    // plus-random string if it isn't (very old Safari).
    const fresh = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(DEVICE_ID_KEY, fresh);
    return fresh;
  } catch (_) {
    // Private mode / storage denied — return an ephemeral id. Backend
    // will just create fresh rows each session; not ideal but not broken.
    return `web-eph-${Date.now()}`;
  }
};

/**
 * Encodes the raw bytes of an existing PushSubscription's applicationServerKey
 * back to the same base64url shape Firebase's VAPID key config uses, so the
 * two can be compared as strings. The subscription's key comes as an
 * ArrayBuffer / Uint8Array; standard btoa produces base64 which we then swap
 * to the URL-safe alphabet and strip padding to match the on-file VAPID.
 */
const urlSafeBase64FromBytes = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/**
 * Registers the FCM service worker with a query string carrying the
 * Firebase config so the SW can initializeApp() at install time (CRA
 * doesn't process public/ files through Webpack, so the SW can't read
 * process.env directly).
 */
const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) return null;
  const params = new URLSearchParams({
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
  });
  const swUrl = `${SW_URL_BASE}?${params.toString()}`;
  try {
    const registration = await navigator.serviceWorker.register(swUrl);
    // register() resolves as soon as the SW is registered, but getToken() calls
    // PushManager.subscribe which requires an *active* SW — a fresh install is
    // still in 'installing' state at this point and subscribe fails with
    // "no active Service Worker". navigator.serviceWorker.ready waits for
    // activation on our scope ('/', from the root SW URL); a returning user
    // with an already-active SW resolves immediately.
    if (!registration.active) await navigator.serviceWorker.ready;
    return registration;
  } catch (err) {
    console.warn('[webPush] service worker registration failed:', err?.message);
    return null;
  }
};

/**
 * One-shot registration flow. Called from the login-completed path in
 * LoginPage. Idempotent — Firebase's getToken returns the same token for
 * the same SW registration + VAPID pair on subsequent calls, and the
 * backend uses SetOptions.merge() to upsert.
 */
export const initWebPush = async (firebaseToken) => {
  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
  if (!vapidKey) {
    console.info('[webPush] REACT_APP_FIREBASE_VAPID_KEY not set — skipping web push registration');
    return { ok: false, reason: 'no-vapid-key' };
  }
  if (typeof Notification === 'undefined') {
    return { ok: false, reason: 'unsupported' };
  }

  const messaging = await getMessagingInstance();
  if (!messaging) return { ok: false, reason: 'unsupported' };

  // Ask permission only when the browser hasn't decided yet. A previously-
  // granted permission returns 'granted' immediately (no prompt); a
  // previously-denied one returns 'denied' without prompting, at which
  // point we just log and give up — re-prompting is disallowed by the
  // browser and would be poor UX anyway.
  let permission = Notification.permission;
  if (permission === 'default') {
    try {
      permission = await Notification.requestPermission();
    } catch (err) {
      console.warn('[webPush] permission request failed:', err?.message);
      return { ok: false, reason: 'permission-error' };
    }
  }
  if (permission !== 'granted') {
    return { ok: false, reason: `permission-${permission}` };
  }

  const registration = await registerServiceWorker();
  if (!registration) return { ok: false, reason: 'sw-failed' };

  // Chrome's PushManager only allows one active PushSubscription per origin+
  // scope. When the browser already has a subscription bound to a different
  // applicationServerKey (from an earlier VAPID key, an aborted first run,
  // or a cross-environment test that landed on the same origin), a fresh
  // getToken() call with the current key throws
  //   "The provided applicationServerKey is not valid."
  // The site-data + notification-permission Clear buttons do NOT purge that
  // subscription — it lives in the browser's push service, not per-site
  // storage — so users can hit this error indefinitely after any config
  // change, even after "clearing everything". Unsubscribe the stale one
  // first; Firebase then creates a matching new subscription cleanly.
  try {
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      const keyBytes = existing.options?.applicationServerKey;
      const encodedKey = keyBytes ? urlSafeBase64FromBytes(keyBytes) : null;
      if (encodedKey !== vapidKey) {
        console.info('[webPush] unsubscribing stale push subscription with mismatched key');
        await existing.unsubscribe();
      }
    }
  } catch (err) {
    // Best-effort — a failure here shouldn't block getToken. If the stale
    // sub survives we just fall back to the previous behavior (error out
    // on getToken and log below).
    console.warn('[webPush] stale-subscription cleanup failed:', err?.message);
  }

  let fcmToken;
  try {
    fcmToken = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });
  } catch (err) {
    console.warn('[webPush] getToken failed:', err?.message);
    return { ok: false, reason: 'get-token-failed' };
  }
  if (!fcmToken) return { ok: false, reason: 'empty-token' };

  const deviceId = getOrCreateDeviceId();
  try {
    await apiClient.post(
      API_ENDPOINTS.fcmToken,
      { fcmToken, platform: 'web', deviceId },
      { headers: { Authorization: `Bearer ${firebaseToken}` } },
    );
  } catch (err) {
    console.warn('[webPush] backend token registration failed:', err?.message);
    return { ok: false, reason: 'backend-registration-failed' };
  }

  console.info('[webPush] registered');
  return { ok: true };
};

/**
 * Foreground push subscription. Fires the callback whenever an SR_* push
 * lands while the tab is focused. Returns an unsubscribe function
 * (idiomatic for useEffect cleanup).
 *
 * The `filter` object narrows delivery:
 *   { serviceRequestId }  — only fire for this SR (used by JobDetailPage
 *                           so an unrelated SR_ push doesn't refetch it).
 *
 * When the filter is omitted the callback fires on every SR_* / legacy
 * SERVICE_REQUEST_CREATED event (used by JobsPage — any SR-lifecycle
 * change can affect the list).
 */
export const subscribeToSrEvents = (callback, filter = null) => {
  if (typeof callback !== 'function') return () => {};
  let unsubscribe = () => {};
  let cancelled = false;

  getMessagingInstance().then((messaging) => {
    if (cancelled || !messaging) return;
    unsubscribe = onMessage(messaging, (payload) => {
      const data = payload?.data || {};
      const type = String(data.type || '');
      const isSrEvent = type.startsWith('SR_') || type === 'SERVICE_REQUEST_CREATED';
      if (!isSrEvent) return;
      if (filter?.serviceRequestId) {
        // Backend puts serviceRequestId in the FCM data map for SR_*
        // events (rialty-notification-service#68). If it's absent
        // (older backend, unusual event shape) fall through and let
        // the caller decide — over-refresh is safe.
        const pushSrId = String(data.serviceRequestId || '');
        if (pushSrId && pushSrId !== filter.serviceRequestId) return;
      }
      callback(payload);
    });
  }).catch((err) => {
    console.warn('[webPush] onMessage setup failed:', err?.message);
  });

  return () => {
    cancelled = true;
    try { unsubscribe(); } catch (_) { /* messaging may be null */ }
  };
};

/**
 * Generic foreground push subscription. Fires the callback for every FCM
 * message that lands while the tab is focused, regardless of type. Callers
 * can filter server-defined event types themselves via the `types` array
 * (case-insensitive prefix + exact-match against `data.type`).
 *
 *   subscribeToPushEvents(cb)                                — every push
 *   subscribeToPushEvents(cb, { types: ['chat_'] })          — chat_* only
 *   subscribeToPushEvents(cb, { types: ['DEADLINE_REMINDER',
 *                                       'DEADLINE_DIGEST'] })
 *   subscribeToPushEvents(cb, { transactionId: 'txn-1' })    — anchored
 *
 * Prefer this over subscribeToSrEvents for any non-SR use case (dashboard
 * refresh, transaction detail live updates, chat unread bump, etc.).
 * SR-specific pages should keep using subscribeToSrEvents — it has SR-
 * specific filter semantics (serviceRequestId).
 *
 * Backend event catalog (rialty-notification-service +
 * rialty-communication-service):
 *   chat_message, group_message, chat_invite
 *   SR_*, SERVICE_REQUEST_CREATED
 *   DEADLINE_REMINDER, DEADLINE_DIGEST
 *   (plus event-source-specific types like task-assigned, offer-submitted,
 *   participant-added, envelope-expiring-soon, envelope-all-signed — types
 *   flow through InternalNotificationController#87 unchanged)
 */
export const subscribeToPushEvents = (callback, filter = null) => {
  if (typeof callback !== 'function') return () => {};
  let unsubscribe = () => {};
  let cancelled = false;

  const wantedTypes = Array.isArray(filter?.types) ? filter.types : null;
  const wantedTxnId = filter?.transactionId ? String(filter.transactionId) : null;

  getMessagingInstance().then((messaging) => {
    if (cancelled || !messaging) return;
    unsubscribe = onMessage(messaging, (payload) => {
      const data = payload?.data || {};
      const type = String(data.type || '');
      if (wantedTypes && !matchesAnyType(type, wantedTypes)) return;
      if (wantedTxnId) {
        // Only fire when the push carries the same transactionId the caller
        // is watching. Missing txnId on the push falls through so the caller
        // can still react — over-refresh is safe.
        const pushTxnId = String(data.transactionId || '');
        if (pushTxnId && pushTxnId !== wantedTxnId) return;
      }
      callback(payload);
    });
  }).catch((err) => {
    console.warn('[webPush] onMessage setup failed:', err?.message);
  });

  return () => {
    cancelled = true;
    try { unsubscribe(); } catch (_) { /* messaging may be null */ }
  };
};

/**
 * Match `type` against a caller-provided list of exact strings and/or
 * suffix-`_` prefixes ("SR_" matches "SR_ACCEPT_REMINDER"). Case-sensitive
 * because the backend event catalog is a mix of upper (DEADLINE_REMINDER,
 * SR_*) and lower (chat_message, group_message) — treating them the same
 * would collide.
 */
const matchesAnyType = (type, wantedTypes) => {
  for (const wanted of wantedTypes) {
    if (!wanted) continue;
    if (wanted.endsWith('_') && type.startsWith(wanted)) return true;
    if (type === wanted) return true;
  }
  return false;
};
